import React from "react";
import { FieldGroupRow, FieldGrounding, GroundingState, RequestAnalysisStatus } from "../../../models/intake";
import "./QuickEntryFieldConfidence.scss";

// Spec #34100 / F1b-rois. Per-row confidence dot + grounding click-to-reveal + extraction-status
// affordance for the Quick Entry row list. Consumes F1a's FieldGroupRow contract (#34098) plus the
// #34100 RequestAnalysis.confidence / .fieldGroundings pass-through; it does NOT edit the shared
// FieldGroupRow block. IntakeResultsDialog is the Classic-mode surface (#34102) and is out of scope.

// Maps a Quick Entry FieldGroupRow id (F1a's stable row ids) to the camelCase RequestAnalysisData
// field name the backend keys confidence/grounding by (ResolvingRequestAnalysisRetriever projects
// FieldIdentifier -> camelCase). Rows with no single corresponding contract field (e.g. the two
// Service Date rows) map to null and render neutral / no-source (AC5).
const ROW_ID_TO_CONFIDENCE_KEY: Readonly<Record<string, string>> = {
    requestTypePurpose: "requestType",
    authorizationDate: "authorizationDate",
    requestDate: "requestDate",
    caseNumber: "caseNumber",
    clinicResponsible: "clinicName",
    sensitiveInfo: "sensitiveInfoDeclarations",
    recordsRequested: "infoReleaseSections",
};

function confidenceKeyForRow(rowId: string): string | null {
    return ROW_ID_TO_CONFIDENCE_KEY[rowId] ?? null;
}

// AC4/AC5: the enum -> color map lives in the UI; ROIS passes GroundingState through verbatim.
// Per TDD #31604 (lines 107/187), non-match extraction fields are green-if-grounded / red-if-not
// and NEVER yellow (yellow is reserved for patient/requestor multiple-match, F1c #34101). Every row
// this component renders is a non-match extraction field, so both NotGrounded and InferredNoSource
// (extracted but not located) map to red; absent/unmapped -> neutral.
function confidenceColorClass(state?: GroundingState): string {
    switch (state) {
        case GroundingState.Grounded:
            return "confidence-green";
        case GroundingState.NotGrounded:
        case GroundingState.InferredNoSource:
            return "confidence-red";
        default:
            return "confidence-neutral";
    }
}

export interface QuickEntryFieldConfidenceProps {
    readonly row: FieldGroupRow;
    readonly status?: RequestAnalysisStatus;
    readonly confidence?: Readonly<Record<string, GroundingState>>;
    readonly fieldGroundings?: readonly FieldGrounding[];
    /** AC6: reveal the source region by scrolling the viewer to the grounding's page. */
    readonly onRevealGrounding?: (grounding: FieldGrounding) => void;
    /** AC7: no-source variant — informational only (e.g. toast), never scrolls. */
    readonly onNoSource?: () => void;
}

/**
 * Confidence + grounding + extraction-status adornment for one Quick Entry row.
 *
 * AC13: confidence/grounding are only meaningful on the live Completed extract path. While an
 * extraction is InProgress the row shows a pending affordance; Failed / RetryExceeded each show a
 * distinct failure affordance; and any other state (no analysis / Unavailable) renders nothing so
 * the row degrades to F1a's existing NeedsEntry display (AC5/AC13 graceful degradation).
 */
const QuickEntryFieldConfidence: React.FC<QuickEntryFieldConfidenceProps> = (props) => {
    const { row, status, confidence, fieldGroundings, onRevealGrounding, onNoSource } = props;

    if (status === RequestAnalysisStatus.InProgress) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--loading"
                title="Extracting…"
                aria-label="Extraction in progress"
            />
        );
    }
    if (status === RequestAnalysisStatus.Failed) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--failed"
                title="Extraction failed"
                aria-label="Extraction failed"
            />
        );
    }
    if (status === RequestAnalysisStatus.RetryExceeded) {
        return (
            <span
                data-testid={`qe-status-${row.id}`}
                className="qe-field-status qe-field-status--retry-exceeded"
                title="Extraction retries exhausted"
                aria-label="Extraction retries exhausted"
            />
        );
    }
    if (status !== RequestAnalysisStatus.Completed) {
        // No analysis data / Unavailable / Available: degrade to F1a's NeedsEntry row, no adornment.
        return null;
    }

    const key = confidenceKeyForRow(row.id);
    const confidenceState = key ? confidence?.[key] : undefined;
    const grounding = key && fieldGroundings ? fieldGroundings.find(g => g.field === key) : undefined;

    return (
        <span className="qe-field-adornments">
            <span
                data-testid={`confidence-${row.id}`}
                className={`qe-confidence-dot ${confidenceColorClass(confidenceState)}`}
                title="Extraction confidence"
            />
            <button
                type="button"
                data-testid={`grounding-${row.id}`}
                className={`qe-grounding ${grounding ? "has-grounding" : "no-source"}`}
                title={grounding ? "Scroll to source in document" : "No source location available"}
                onClick={() => (grounding ? onRevealGrounding?.(grounding) : onNoSource?.())}
            >
                <span className="qe-grounding__label">{grounding ? "Source" : "No source"}</span>
            </button>
        </span>
    );
};

export default QuickEntryFieldConfidence;
